import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Box,
  Typography,
  useTheme,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import {
  FileText,
  FileSpreadsheet,
  Mail,
  Users,
  Clock,
  BookOpen,
  Settings,
  BarChart3,
  Check,
} from 'lucide-mui';

const mockTemplateContent = `# Meeting Minutes

**Date:** March 15, 2024  
**Time:** 10:00 AM - 11:00 AM  
**Meeting Type:** Weekly Team Planning

## Attendees
- Sarah Chen (Product Manager)
- Mike Rodriguez (Lead Developer) 
- Jennifer Kim (UX Designer)

## Agenda Items

### 1. Website Redesign Update
Sarah presented the current progress on the website redesign project. The team has successfully completed user research and wireframe development.

**Key Updates:**
- User research phase completed with 50+ interviews
- Wireframes approved by stakeholders 
- Design system foundation established

### 2. Mobile App Development Status
Mike provided an update on the mobile application development. Core functionality development is complete and testing phase has begun.

## Decisions Made

1. **Launch Timeline:** Approved moving the product launch date to April 30th to allow additional testing time

2. **Resource Allocation:** Assigned one additional designer to the website project to meet quality standards

## Action Items

| Task | Assigned To | Due Date |
|------|-------------|----------|
| Complete visual design mockups | Jennifer Kim | March 22 |
| Finish performance optimization | Mike Rodriguez | March 25 |
| Schedule user testing sessions | Alex Thompson | March 20 |

---
*Minutes recorded by Sarah Chen*`;

// Mock templates data
const mockTemplates = [
  {
    id: 'meeting-minutes',
    title: 'Meeting Minutes',
    description: 'Professional meeting documentation and action items',
    icon: Users,
    featured: true,
  },
  {
    id: 'project-summary',
    title: 'Project Summary',
    description: 'Comprehensive project overview with key milestones',
    icon: FileText,
    featured: true,
  },
  {
    id: 'sop',
    title: 'SOP',
    description: 'Standard Operating Procedure with step-by-step instructions',
    icon: Settings,
    featured: true,
  },
  {
    id: 'analysis',
    title: 'Analysis',
    description: 'Detailed analysis report with insights and recommendations',
    icon: BarChart3,
    featured: true,
  },
  {
    id: 'email-summary',
    title: 'Email Summary',
    description: 'Concise email digest with key points and actions',
    icon: Mail,
    featured: false,
  },
  {
    id: 'report-template',
    title: 'Report Template',
    description: 'Structured report format with data analysis',
    icon: FileSpreadsheet,
    featured: false,
  },
  {
    id: 'timeline-summary',
    title: 'Timeline Summary',
    description: 'Chronological breakdown of events and milestones',
    icon: Clock,
    featured: false,
  },
  {
    id: 'study-notes',
    title: 'Study Notes',
    description: 'Educational content breakdown with key concepts',
    icon: BookOpen,
    featured: false,
  },
];

const TemplatesDemo: React.FC = () => {
  const theme = useTheme();
  const [templateResponse, setTemplateResponse] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [demoPhase, setDemoPhase] = useState(1); // 1: template selection, 2: streaming, 3: complete, 4: download overlay
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const streamingRef = useRef<number | null>(null);
  const currentIndexRef = useRef(0);

  // Demo sequence management
  useEffect(() => {
    let timer: number;

    switch (demoPhase) {
      case 1: {
        // Show template selection for 2 seconds, then auto-select
        timer = window.setTimeout(() => {
          setDemoPhase(2);
        }, 2000);
        break;
      }
      case 2: {
        // Start streaming immediately when template is selected
        if (!isStreaming) {
          setIsStreaming(true);
        }
        break;
      }
      case 3: {
        // Content generation complete, show download overlay
        timer = window.setTimeout(() => {
          setShowDownloadModal(true);
          setDemoPhase(4);
        }, 500);
        break;
      }
      case 4: {
        // Download overlay shown, wait then restart
        timer = window.setTimeout(() => {
          setShowDownloadModal(false);
          setTemplateResponse('');
          setDemoPhase(1);
          setIsStreaming(false);
        }, 2500);
        break;
      }
      default:
        break;
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [demoPhase, isStreaming]);

  // Streaming text generation
  useEffect(() => {
    if (demoPhase === 2 && isStreaming) {
      setTemplateResponse('');
      currentIndexRef.current = 0;

      const streamText = () => {
        const words = mockTemplateContent.split(' ');

        const addNextWord = () => {
          if (currentIndexRef.current < words.length) {
            const currentWords = words.slice(0, currentIndexRef.current + 1);
            setTemplateResponse(currentWords.join(' '));
            currentIndexRef.current += 1;

            // Much faster speed - reduced delays significantly
            const currentWord = words[currentIndexRef.current - 1];
            let delay = 8; // Base delay (was 17ms, now 8ms for much faster speed)

            if (currentWord?.includes('\n\n')) {
              delay = 25; // Pause at paragraph breaks (was 67ms, now 25ms)
            } else if (currentWord?.includes('\n')) {
              delay = 20; // Pause at line breaks (was 50ms, now 20ms)
            } else if (
              currentWord?.includes('.') ||
              currentWord?.includes('!') ||
              currentWord?.includes('?')
            ) {
              delay = 15; // Pause at sentence endings (was 60ms, now 15ms)
            } else if (
              currentWord?.includes(',') ||
              currentWord?.includes(':')
            ) {
              delay = 12; // Pause at commas/colons (was 33ms, now 12ms)
            } else if (currentWord?.length > 8) {
              delay = 10; // Slightly slower for longer words (was 27ms, now 10ms)
            }

            streamingRef.current = window.setTimeout(addNextWord, delay);
          } else {
            // Streaming complete
            setIsStreaming(false);
            setDemoPhase(3);
          }
        };

        addNextWord();
      };

      streamText();
    }

    return () => {
      if (streamingRef.current) {
        clearTimeout(streamingRef.current);
      }
    };
  }, [demoPhase, isStreaming]);

  const handleTemplateSelect = () => {
    setDemoPhase(2);
  };

  const featuredTemplates = mockTemplates.filter(
    (template) => template.featured
  );

  const renderTemplateItem = (template: any, index: number) => (
    <ListItemButton
      key={template.id}
      onClick={() => handleTemplateSelect()}
      sx={{
        py: 1.5,
        px: 2,
        transition: theme.transitions.create('background-color'),
        '&:hover': {
          bgcolor: theme.palette.action.hover,
        },
        height: 'auto',
        minHeight: '72px',
        cursor: 'pointer',
        borderBottom: index < featuredTemplates.length - 1 ? 1 : 0,
        borderColor: 'secondary.light',
        borderRadius: 0,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mr: 2,
          color: theme.palette.primary.main,
        }}
      >
        <template.icon size={20} />
      </Box>
      <ListItemText
        primary={
          <Typography
            variant="body2"
            sx={{
              fontWeight: 400,
              color: 'text.primary',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              lineHeight: 1.2,
              mb: 0.5,
              whiteSpace: 'normal',
              height: 'auto',
              pr: 4,
              fontSize: '0.875rem !important',
            }}
          >
            {template.title}
          </Typography>
        }
        secondary={
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              fontSize: '0.7rem !important',
              lineHeight: 1.4,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              whiteSpace: 'normal',
            }}
          >
            {template.description}
          </Typography>
        }
        primaryTypographyProps={{
          variant: 'body2',
          fontWeight: 400,
          color: 'text.primary',
          sx: {
            fontSize: '0.875rem !important',
          },
        }}
        secondaryTypographyProps={{
          variant: 'caption',
          color: 'text.secondary',
          sx: {
            fontSize: '0.7rem !important',
          },
        }}
        sx={{
          my: 0,
          overflow: 'hidden',
          width: '100%',
          pr: 1,
        }}
      />
    </ListItemButton>
  );

  return (
    <Box
      sx={{
        p: 3,
        minHeight: 500,
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            mb: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <FileText sx={{ fontSize: 20 }} />
          Create Documents
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Turn your recordings into summaries and reports
        </Typography>
      </Box>

      {/* Template Selection Phase */}
      {demoPhase === 1 && (
        <Box sx={{ position: 'relative', height: '100%' }}>
          <List disablePadding sx={{ maxWidth: 500, mx: 'auto' }}>
            {featuredTemplates.map((template, index) => (
              <React.Fragment key={template.id}>
                {renderTemplateItem(template, index)}
              </React.Fragment>
            ))}
          </List>
        </Box>
      )}

      {/* Content Generation Phase */}
      {(demoPhase === 2 || demoPhase === 3 || demoPhase === 4) && (
        <Box sx={{ position: 'relative', height: '100%' }}>
          <Box
            sx={{
              height: '100%',
              overflowY: 'auto',
              backgroundColor: theme.palette.background.paper,
              minHeight: 300,
              position: 'relative',
            }}
          >
            <Box sx={{ position: 'relative' }}>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: ({ children }) => (
                    <Typography
                      variant="h4"
                      component="h1"
                      sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}
                    >
                      {children}
                    </Typography>
                  ),
                  h2: ({ children }) => (
                    <Typography
                      variant="h5"
                      component="h2"
                      sx={{
                        fontWeight: 600,
                        mb: 1.5,
                        mt: 2,
                        color: 'text.primary',
                      }}
                    >
                      {children}
                    </Typography>
                  ),
                  h3: ({ children }) => (
                    <Typography
                      variant="h6"
                      component="h3"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        mt: 1.5,
                        color: 'text.primary',
                      }}
                    >
                      {children}
                    </Typography>
                  ),
                  p: ({ children }) => (
                    <Typography
                      variant="body1"
                      sx={{ mb: 1.5, lineHeight: 1.6, color: 'text.secondary' }}
                    >
                      {children}
                    </Typography>
                  ),
                  ul: ({ children }) => (
                    <Box
                      component="ul"
                      sx={{ mb: 1.5, pl: 2, color: 'text.secondary' }}
                    >
                      {children}
                    </Box>
                  ),
                  ol: ({ children }) => (
                    <Box
                      component="ol"
                      sx={{ mb: 1.5, pl: 2, color: 'text.secondary' }}
                    >
                      {children}
                    </Box>
                  ),
                  li: ({ children }) => (
                    <Box component="li" sx={{ mb: 0.5 }}>
                      {children}
                    </Box>
                  ),
                  table: ({ children }) => (
                    <Box
                      component="table"
                      sx={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        mb: 2,
                        '& th, & td': {
                          border: `1px solid ${theme.palette.divider}`,
                          padding: '8px 12px',
                          textAlign: 'left',
                        },
                        '& th': {
                          backgroundColor: 'background.default',
                          fontWeight: 600,
                        },
                      }}
                    >
                      {children}
                    </Box>
                  ),
                  strong: ({ children }) => (
                    <Box component="strong" sx={{ fontWeight: 600 }}>
                      {children}
                    </Box>
                  ),
                  em: ({ children }) => (
                    <Box component="em" sx={{ fontStyle: 'italic' }}>
                      {children}
                    </Box>
                  ),
                  code: ({ children }) => (
                    <Box
                      component="code"
                      sx={{
                        backgroundColor: 'background.default',
                        padding: '2px 4px',
                        borderRadius: '4px',
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                      }}
                    >
                      {children}
                    </Box>
                  ),
                  hr: () => (
                    <Box
                      sx={{
                        border: 'none',
                        borderTop: `1px solid ${theme.palette.divider}`,
                        my: 2,
                      }}
                    />
                  ),
                  a: ({ href, children }) => {
                    // Handle citation links
                    if (href && href.match(/^\d+$/)) {
                      return (
                        <Box
                          component="span"
                          sx={{
                            color: 'primary.main',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            '&:hover': {
                              color: 'primary.dark',
                            },
                          }}
                        >
                          {children}
                        </Box>
                      );
                    }
                    return (
                      <Box
                        component="a"
                        href={href}
                        sx={{
                          color: 'primary.main',
                          textDecoration: 'underline',
                          '&:hover': {
                            color: 'primary.dark',
                          },
                        }}
                      >
                        {children}
                      </Box>
                    );
                  },
                }}
              >
                {templateResponse}
              </ReactMarkdown>
              {/* Streaming cursor */}
              {isStreaming && (
                <Box
                  component="span"
                  sx={{
                    display: 'inline-block',
                    width: '2px',
                    height: '1.2em',
                    backgroundColor: 'primary.main',
                    ml: 0.5,
                    animation: 'blink 1s infinite',
                    '@keyframes blink': {
                      '0%, 50%': { opacity: 1 },
                      '51%, 100%': { opacity: 0 },
                    },
                  }}
                />
              )}
            </Box>

            {/* Download Overlay - positioned over the template content */}
            {showDownloadModal && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  bgcolor: 'background.paper',
                  borderRadius: 2,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                  border: `1px solid ${theme.palette.divider}`,
                  p: 4,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                  minWidth: 280,
                  zIndex: 10,
                  animation: 'fadeInScale 0.3s ease-out',
                  '@keyframes fadeInScale': {
                    '0%': {
                      opacity: 0,
                      transform: 'translate(-50%, -50%) scale(0.9)',
                    },
                    '100%': {
                      opacity: 1,
                      transform: 'translate(-50%, -50%) scale(1)',
                    },
                  },
                }}
              >
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    backgroundColor: theme.palette.success.main,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1,
                  }}
                >
                  <Check
                    sx={{
                      fontSize: 32,
                      color: 'white',
                    }}
                  />
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    textAlign: 'center',
                  }}
                >
                  Downloaded as PDF
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    textAlign: 'center',
                  }}
                >
                  Your document has been saved successfully
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default TemplatesDemo;
